import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import styles from "./LicenseList.module.css";
import { AxiosInstance } from "../../services/Api";
import PopupModal from "../../components/PopupModal/PopupModal";

const LicenseList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [error, setError] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",   // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Fetch licenses from API
  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.get(
        `/licenses?page=${page}&limit=${limit}`
      );

      if (!response || response.status !== 200) {
        throw new Error("Failed to fetch licenses");
      }

      const result = response.data;

      if (result?.success) {
        // Set licenses data
        setLicenses(result?.data?.licenses || []);

        // Set pagination data
        if (result?.data?.pagination) {
          setPagination({
            current_page: result.data.pagination.current_page || 1,
            per_page: result.data.pagination.per_page || 10,
            total: result.data.pagination.total || 0,
            total_pages: result.data.pagination.total_pages || 0,
            has_next: result.data.pagination.has_next || false,
            has_prev: result.data.pagination.has_prev || false,
          });
        }
      } else {
        throw new Error(result?.message || "Failed to fetch licenses");
      }
    } catch (err) {
      console.error("Error fetching licenses:", err);
      setError(
        err?.response?.data?.message || err?.message || "Something went wrong"
      );
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  // Fetch data when page or limit changes
  useEffect(() => {
    fetchLicenses();
  }, [page, limit, fetchLicenses]);

  // Resend activation email API
  const handleResendActivation = useCallback(async (row) => {
    if (!row?.id) {
      alert("Invalid license ID");
      return;
    }

    alert(
      `Are you sure you want to resend activation email to ${
        row?.email || row?.name
      }?`
    );

    setResendingEmail(row.id);

    try {
      const response = await AxiosInstance.post(
        `/licenses/${row.id}/resend-activation`
      );

      if (response?.status === 200 && response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message: response?.data?.message || "Activation email sent successfully!",
          redirectPath: "",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Failed to send activation email",
          redirectPath: "",
        });

        throw new Error(
          response?.data?.message || "Failed to send activation email"
        );
      }
    } catch (err) {
      console.error("Error resending activation:", err);

      setModalState({
        open: true,
        type: "error",
        message: err?.response?.data?.message ||
          err?.message ||
          "Failed to send activation email",
        redirectPath: "",
      });
    } finally {
      setResendingEmail(null);
    }
  }, []);

  // Handle edit license - navigate to licenseCreate with ID
  const handleEditLicense = useCallback(
    (row) => {
      if (row?.id) {
        navigate(`/dashboard/licenseCreate?id=${row.eid}`);
      }
    },
    [navigate]
  );

  // Helper functions
  const getAccountTypeLabel = (accountType) => {
    return accountType === 1 ? "Paid" : "Free";
  };

  const getValidationStatus = (isValidated) => {
    return isValidated === 1 ? "Yes" : "No";
  };

  // Table columns configuration
  const columns = [
    { key: "client_id", label: "CLIENT ID", width: "100px" },
    { key: "name", label: "NAME", minWidth: "140px" },
    {
      key: "school_name",
      label: "SCHOOL NAME",
      minWidth: "180px",
      render: (value) => value || "-",
    },
    {
      key: "email",
      label: "EMAIL",
      minWidth: "200px",
      render: (value) => value || "-",
    },
    {
      key: "client_mobile",
      label: "MOBILE",
      width: "120px",
      render: (value) => value || "-",
    },
    {
      key: "role_display_name",
      label: "USER ROLE",
      minWidth: "130px",
      render: (value) => value || "-",
    },
    {
      key: "account_type",
      label: "ACCOUNT TYPE",
      width: "130px",
      render: (value) => getAccountTypeLabel(value),
    },
    {
      key: "is_validated",
      label: "IS VALIDATED",
      width: "110px",
      render: (value) => getValidationStatus(value),
    },
  ];

  // Action buttons configuration
  const actionButtons = [
    {
      id: "edit",
      label: "Edit",
      icon: <i className="fa fa-edit" />,
      variant: "edit",
      tooltip: "Edit License",
      onClick: (row) => handleEditLicense(row),
      // condition: (row) => row.account_type !== 1,
    },
    {
      id: "email",
      label: "Send Email",
      icon: <i className="fa fa-envelope" />,
      variant: "email",
      tooltip: "Resend Activation Email",
      onClick: (row) => handleResendActivation(row),
      loading: (row) => resendingEmail === row.id,
    },
  ];

  // Event handlers
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleCreateLicense = () => {
    setMenuOpen(false);
    navigate("/dashboard/licenseCreate");
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (

    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>LICENSE LIST</h1>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.menuButton}
              onClick={toggleMenu}
              aria-label="More options"
            >
              <i
                className="fa-solid fa-ellipsis-vertical"
                style={{ fontSize: "18px", color: "#333" }}
              />
            </button>
            {menuOpen && (
              <div className={styles.dropdownMenu}>
                <button className={styles.menuItem} onClick={handleCreateLicense}>
                  Create license
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
            <button onClick={fetchLicenses} className={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        <div className={styles.tableSection}>
          <CustomTable
            columns={columns}
            rows={licenses}
            actionButtons={actionButtons}
            loading={loading}
            emptyMessage="No licenses found"
            hoverable
            rowKeyField="id"
          />
        </div>

        {/* Pagination */}
        <div className={styles.paginationSection}>
          <CustomPagination
            page={pagination.current_page}
            limit={pagination.per_page}
            total={pagination.total}
            onPageChange={handlePageChange}
            showLimitSelector={false}
            siblingCount={3}
            showFirstLast
            showPrevNext
          />
        </div>
      </div>
    </>


  );
};

export default LicenseList;
